/**
 * The two footnote lines closing the Manning Stats text — what the latest Add Officers calls
 * brought in — e.g. "Inclusive of 04 Bus SOS, 07 Bikes SOS" and "Inclusive of 05 OT (1130-1530)".
 *
 * SOS and OT are tracked separately (RosterState.last_added), so an OT-only add leaves the SOS
 * line standing. The OT figure is every OT officer at the last-added timing, not only those the
 * call added; the SOS figure is the last batch, per deployment. Counted against the roster as it
 * is now, so officers since removed (or an undone add) drop out; a line with none left is blank.
 * Each line is shown with its own checkbox and only copied when ticked (see StatsFooterLines).
 */
import type { OfficerMetadata } from '@/types/api';

const pad = (n: number) => String(n).padStart(2, '0');

const titleCase = (s: string) => s.toLowerCase().replace(/(^|\s)\S/g, (c) => c.toUpperCase());

export interface FooterLines { sos: string; ot: string }

export function lastAddFooterLines(
  lastAdded: Record<string, string[]> | undefined,
  officerMetadata: Record<string, OfficerMetadata> | undefined,
  /** Officer keys on the version being shown. */
  presentKeys: Iterable<string>,
): FooterLines {
  if (!lastAdded) return { sos: '', ot: '' };
  const present = new Set(presentKeys);

  const otParts: string[] = [];
  for (const timing of lastAdded.ot ?? []) {
    let n = 0;
    for (const key of present) if (key.startsWith('OT') && key.endsWith(`_${timing}`)) n++;
    if (n > 0) otParts.push(`${pad(n)} OT (${timing})`);
  }

  const byDeployment = new Map<string, number>();
  for (const key of lastAdded.sos ?? []) {
    if (!present.has(key)) continue;
    const dep = titleCase((officerMetadata?.[key]?.deployment ?? '').trim()) || 'Unassigned';
    byDeployment.set(dep, (byDeployment.get(dep) ?? 0) + 1);
  }
  const sosParts = [...byDeployment].map(([dep, n]) => `${pad(n)} ${dep} SOS`);

  const line = (parts: string[]) => (parts.length > 0 ? `Inclusive of ${parts.join(', ')}` : '');
  return { sos: line(sosParts), ot: line(otParts) };
}

/** `stats` with the given footnote lines at the end, each on its own line; unchanged when none. */
export function withFooter(stats: string, lines: string[]): string {
  const kept = lines.filter(Boolean);
  return kept.length > 0 && stats ? `${stats.trimEnd()}\n\n${kept.join('\n')}` : stats;
}
